// # Please ensure understanding of the pricing page public setup, documented here:
// # https://product.hubteam.com/docs/growth-ssm-handbook/docs/dotcom/overview.html
// # before making changes to this file or merging in mothership changes

import { Application } from 'foundations-components/components/Application';
import { renderRoot } from 'quartz-core/client';
import { createBrowserHistory, Router } from 'quartz-routing/react-router';
import { RhumbProvider } from 'react-rhumb';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import BasicPublicI18nSetup from 'I18n/init/setup/BasicPublicI18nSetup';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import Raven from 'raven-js';
import { DataFetchingClientProvider } from 'data-fetching-client';
import { dataFetchingClient } from './dataFetchingClient';
import PublicRoutes from './PublicRoutes';
import { isPricingPreviewPath } from './constants/routes';
import { CurrencyContextProvider as PublicCurrencyContextProvider } from './context/CurrencyContext';
import { ExperimentProvider } from './context/ExperimentContext';
import { PublicI18nContext } from './context/I18nContext';
import PublicRootLayout from './layouts/PublicRootLayout';
import { setupMetricsFactory } from './utils/metrics';
import { setupMetricsFactory as setupHsocMetricsFactory } from 'pricing-pages-ui-hsoc/components/plan-quiz/metrics';
import { ERROR_SEVERITY, ErrorBoundary } from './components/error-boundary/ErrorBoundary';
import { PUBLIC_APP_ROOT } from './components/error-boundary/ErrorBoundaryNames';
import { PublicAppErrorFallback } from './components/error-boundary/PublicAppErrorFallback';
import rhumbConfig from './rhumbConfig';
import AsyncPublicDebugPanel from './components/debug-panel/AsyncPublicDebugPanel';
import { fetchPublicPricingPageDetails } from './setup/fetchPublicPricingPageDetails';
import { fetchUserData } from './setup/fetchUserData';
import { applyI18nDebugOverride, getManualI18nLang } from './setup/i18nGlobals';
import { redirectNonProxied } from './setup/redirectNonProxied';
import { setupChatTracking } from './setup/setupChatTracking';
import { setupEventTracker, setupNoopEventTracker } from './setup/setupEventTracker';
import { setupRavenFilters } from './setup/setupRavenFilters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function start(rootUrl, {
  Routes = PublicRoutes,
  shouldRedirectNonProxied = true
} = {}) {
  // Raven first so any later thrown error flows through our filters.
  setupRavenFilters();
  // Metrics factory before anything that might emit a counter, including
  // redirectNonProxied below.
  const isPreview = isPricingPreviewPath();
  if (!isPreview) {
    setupMetricsFactory('pricing-pages-ui-public-app');
    setupHsocMetricsFactory('pricing-pages-ui-public-app');
  }
  // Sync debug i18n override into window global before anything reads it.
  applyI18nDebugOverride();

  // English translations are inlined in the main bundle via i2l/sporks,
  // so they register synchronously below. Non-English locale files are
  // fetched over the network; langAvailable resolves when they arrive.
  const i18nManualLang = getManualI18nLang();
  const langAvailable = BasicPublicI18nSetup.setup({
    lang,
    manualLocaleSettings: i18nManualLang ? {
      locale: i18nManualLang,
      langEnabled: true
    } : undefined
  });

  // Non-critical boot steps. Chat tracking defers itself via
  // requestIdleCallback; event tracker is sync and cheap.
  if (isPreview) {
    setupNoopEventTracker();
  } else {
    setupChatTracking();
    setupEventTracker();
  }

  // Snapshot search params before the router starts — React Router
  // redirects (e.g. "/" → "/marketing") run through createToObject which
  // strips params like forceRedirect that redirectNonProxied needs.
  const originalSearch = window.location.search;

  // Wait for i18n registration before mounting the React tree. Even though
  // English strings are inlined via sporks, provider.register() is async
  // and resolves on the next microtask. Mounting before it settles causes
  // "Missing translation" errors for components like LoadingSpinner that
  // render immediately (e.g. during Loadable chunk fetches).
  return langAvailable.then(() => {
    startRouter(rootUrl, BasicPublicI18nSetup.provider, false, Routes);
    if (shouldRedirectNonProxied) {
      redirectNonProxied(originalSearch);
    }
  }, error => {
    Raven.captureException(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['failed-to-bootstrap-public-app-i18n']
    });
    startRouter(rootUrl, BasicPublicI18nSetup.provider, true, Routes);
  });
}
export function startRouter(rootUrl, i18nLoader, hasError = false, Routes = PublicRoutes) {
  if (hasError) {
    renderRoot( /*#__PURE__*/_jsx(Application, {
      name: "pricing-pages-ui-public-app",
      children: /*#__PURE__*/_jsx(PublicI18nContext.Provider, {
        value: {
          i18nLoader
        },
        children: /*#__PURE__*/_jsx(PublicAppErrorFallback, {})
      })
    }));
    return;
  }
  const history = createBrowserHistory({
    basename: rootUrl
  });
  fetchUserData();
  fetchPublicPricingPageDetails();
  renderRoot( /*#__PURE__*/_jsx(Application, {
    name: "pricing-pages-ui-public-app",
    children: /*#__PURE__*/_jsxs(DataFetchingClientProvider, {
      client: dataFetchingClient,
      children: [/*#__PURE__*/_jsx(ExperimentProvider, {
        children: /*#__PURE__*/_jsx(PublicI18nContext.Provider, {
          value: {
            i18nLoader
          },
          children: /*#__PURE__*/_jsx(RhumbProvider, {
            history: historyAdapter(history),
            config: rhumbConfig,
            children: /*#__PURE__*/_jsx(Router, {
              history: history,
              children: /*#__PURE__*/_jsx(ErrorBoundary, {
                errorBoundaryName: PUBLIC_APP_ROOT,
                severity: ERROR_SEVERITY.HIGH,
                FallbackComponent: PublicAppErrorFallback,
                children: /*#__PURE__*/_jsx(PublicCurrencyContextProvider, {
                  children: /*#__PURE__*/_jsx(PublicRootLayout, {
                    children: /*#__PURE__*/_jsx(Routes, {})
                  })
                })
              })
            })
          })
        })
      }), /*#__PURE__*/_jsx(AsyncPublicDebugPanel, {})]
    })
  }));
}